import { useMemo, useRef } from 'react';

/**
 * Load More accumulation for a server-paginated list (offer builder focus
 * grid, Asana 1219788320590626 ruling 3): pages 1..N of one query signature
 * are kept and flattened in order; a change of signature (search, filters,
 * sort, mode, side, page size) drops every page, which is the stale-page
 * hazard the profile tabs document at ProfileWantListTab.tsx:347.
 *
 * RTK Query owns the cache, so this only remembers which pages have been
 * SEEN for the current signature; a page refetched from cache is re-stored
 * idempotently.
 */
export const usePagedAccumulation = <T,>(
  signature: string,
  page: number,
  pageRows: T[] | undefined,
): T[] => {
  const ref = useRef<{ signature: string; pages: Map<number, T[]> }>({
    signature,
    pages: new Map(),
  });
  if (ref.current.signature !== signature) {
    ref.current = { signature, pages: new Map() };
  }
  if (pageRows) ref.current.pages.set(page, pageRows);

  return useMemo(() => {
    const out: T[] = [];
    for (let p = 1; p <= page; p += 1) {
      const rows = ref.current.pages.get(p);
      if (rows) out.push(...rows);
    }
    return out;
    // ref.current is keyed by signature + page; pageRows is the only input that
    // changes the stored page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, page, pageRows]);
};

export default usePagedAccumulation;
